<template>
    <div>
        <h2 class="font-header text-[3rem] font-bold text-primary mb-6">Health</h2>

        <div class="bg-white rounded-lg shadow-md p-6">
            <div v-if="isLoading" class="text-center py-8">
                <Loader size="medium" />
            </div>

            <div v-else class="space-y-6">
                <div>
                    <h3 class="font-header text-[1.5rem] font-bold mb-2">Email</h3>
                    <p class="font-body text-[1.125rem] leading-[1.75] text-gray-700">
                        Sends a test email to all admin accounts to confirm that website emails are being delivered.
                    </p>
                </div>

                <fieldset class="font-body text-[1.125rem] leading-[1.75]">
                    <legend class="text-gray-500 mb-2">Send to</legend>
                    <div class="space-y-2">
                        <label class="flex items-center gap-3 text-primary cursor-pointer">
                            <input v-model="selectedRecipient" type="radio" name="test-email-recipient" :value="ALL_ADMINS"
                                class="h-4 w-4 accent-green-700" />
                            All admin accounts ({{ recipients.length }})
                        </label>
                        <label v-for="email in recipients" :key="email"
                            class="flex items-center gap-3 text-primary cursor-pointer break-all">
                            <input v-model="selectedRecipient" type="radio" name="test-email-recipient" :value="email"
                                class="h-4 w-4 accent-green-700 flex-shrink-0" />
                            {{ email }}{{ isCurrentAdmin(email) ? ' (you)' : '' }}
                        </label>
                    </div>
                </fieldset>

                <div v-if="result" :class="[
                    'rounded-md p-4 font-body text-[1.125rem] leading-[1.75]',
                    result.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                ]">
                    {{ result.message }}
                </div>

                <div class="flex justify-end pt-4 border-t border-gray-200">
                    <button @click="sendTestEmail" :disabled="isSending"
                        class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-[1.125rem] leading-[1.75] disabled:opacity-50 disabled:cursor-not-allowed">
                        {{ isSending ? 'Sending...' : 'Send Test Email' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { adminService } from '../../services/admin'
import Loader from '../Loader.vue'
import { useLoadingState } from '../../composables/useLoadingState'
import { formatDateTime } from '../../utils/date'

const ALL_ADMINS = 'all'

const status = ref({})
const selectedRecipient = ref(ALL_ADMINS)
const recipients = computed(() => status.value.recipients || [])

const isCurrentAdmin = (email) =>
    email.toLowerCase() === (status.value.current_admin || '').toLowerCase()

// Default to the signed-in admin; fall back to everyone if they aren't listed.
const selectDefaultRecipient = () => {
    selectedRecipient.value = recipients.value.find(isCurrentAdmin) || ALL_ADMINS
}
const result = ref(null)
const isSending = ref(false)
const { loading: isLoading, setLoading } = useLoadingState(false)

const loadStatus = async () => {
    try {
        status.value = await adminService.getEmailHealth()
    } catch (error) {
        console.error('Failed to load email health:', error)
        status.value = {}
    }
}

const sendTestEmail = async () => {
    isSending.value = true
    result.value = null
    try {
        const recipient = selectedRecipient.value === ALL_ADMINS ? null : selectedRecipient.value
        const response = await adminService.sendTestEmail(recipient)
        result.value = {
            success: true,
            message: `Test email sent to ${response.recipients.join(', ')} at ${formatDateTime(response.sent_at)}. Check the inbox.`
        }
    } catch (error) {
        console.error('Test email failed:', error)
        const detail = error.response?.data?.error || error.message
        result.value = {
            success: false,
            message: `Email sending failed: ${detail}. Check the Brevo API key.`
        }
    } finally {
        isSending.value = false
    }
}

onMounted(async () => {
    setLoading(true)
    await loadStatus()
    selectDefaultRecipient()
    setLoading(false)
})
</script>

<style scoped></style>
